import React, { memo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { ThreeDots } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';

import CommonButton, { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import WithError from '../../../components/with-error/WithError';
import CommonTextarea from '../../../components/common-textarea/CommonTextarea';
import CommonIconButton from '../../../components/common-icon-button/CommonIconButton';
import { ICONS } from '../../../icons';
import Icon from '../../../icons/Icon';

import scss from './CreateMessageModal.module.scss';

const CreateMessageModalForm = ({ onSubmit, onCancel }) => {
  const { t } = useTranslation('scout');
  const { submitting } = useFormState();

  const { errors } = useFormState();

  const messagesFields = useFieldArray('messages').fields;

  const handlePasteText = (index, event) => {
    event.preventDefault();

    const clipboardData = event.clipboardData || window.clipboardData;

    const pastedText =
      (clipboardData &&
        (clipboardData.getData?.('text/plain') ||
          clipboardData.getData?.('text') ||
          clipboardData.getData?.('Text'))) ||
      '';

    const normalizedText = pastedText.replace(/\u2028|\u2029/g, '\n').replace(/\r\n?/g, '\n');

    const parsedMessages = normalizedText
      .split(/\n\n/)
      .map((message) => message.trim())
      .filter(Boolean);

    parsedMessages.forEach((message, i) => {
      if (i === 0) {
        const existedValue = messagesFields.value[index].message ?? '';
        messagesFields.update(index, { message: existedValue + message });
      } else {
        messagesFields.push({ message });
      }
    });
  };

  const handleAddNewMessage = () => {
    messagesFields.push({ message: '' });
  };

  const handleRemoveMessage = (index) => {
    messagesFields.remove(index);
  };

  return (
    <div className={scss.form}>
      <h3 className={scss.title}>{t('addMessage')}</h3>

      <div className={scss.content}>
        <FieldArray name='messages'>
          {({ fields }) =>
            fields.map((name, index) => (
              <div className={scss.inputRow} key={`${name}.${index}.message`}>
                <div className={scss.inputHeader}>
                  <label>{`${t('message')} ${fields.length > 1 ? ` ${index + 1}:` : ' :'}`}</label>

                  {fields.length > 1 ? (
                    <CommonIconButton
                      tooltip={t('remove')}
                      icon={ICONS.Delete}
                      onClick={() => handleRemoveMessage(index)}
                    />
                  ) : null}
                </div>

                <Field name={`${name}.message`}>
                  {({ input, meta }) => (
                    <WithError
                      isShowError={meta.touched && errors[`${name}.message`]}
                      errorText={t(errors[`${name}.message`])}>
                      <CommonTextarea
                        name={input.name}
                        value={input.value}
                        onChange={input.onChange}
                        onBlur={input.onBlur}
                        onFocus={input.onFocus}
                        placeholder={t('enterYourMessage')}
                        disabled={submitting}
                        rows={4}
                        isError={meta.touched && errors[`${name}.message`]}
                        onPaste={(e) => handlePasteText(index, e)}
                        enableVoiceInput
                      />
                    </WithError>
                  )}
                </Field>
              </div>
            ))
          }
        </FieldArray>

        <CommonButton maxWidth={160} styleColor={BUTTON_STYLES.SECONDARY} onClick={handleAddNewMessage}>
          <Icon icon={ICONS.CirclePlus} />
          {t('moreMessages')}
        </CommonButton>
      </div>

      <div className={scss.actions}>
        <CommonButton styleColor={BUTTON_STYLES.SECONDARY} maxWidth={150} onClick={onCancel}>
          {t('cancel')}
        </CommonButton>

        <CommonButton
          styleColor={BUTTON_STYLES.PRIMARY}
          type='submit'
          maxWidth={160}
          onClick={onSubmit}
          disabled={submitting}>
          {submitting ? <ThreeDots height={15} width={40} /> : null}
          {t('add')}
        </CommonButton>
      </div>
    </div>
  );
};

export default memo(CreateMessageModalForm);
